import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, Target, AlertTriangle, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import HelpButton from "../components/HelpButton";

const CATEGORIES = ["Food", "Groceries", "Transport", "Shopping", "Entertainment", "Bills", "Subscriptions", "Gifts", "Other"];

export default function Budgets() {
  const { user } = useAuth();
  const canSmart = user?.modules?.smart_budgets === true;
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ category: "Food", monthly_limit: "" });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const load = async () => {
    const { data } = await api.get("/budgets/status");
    setStatus(data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", {
        category: form.category,
        monthly_limit: parseFloat(form.monthly_limit),
      });
      toast.success("Budget saved");
      setForm({ ...form, monthly_limit: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/budgets/${id}`);
    load();
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get("/budgets/suggestions");
      if (!data.suggestions?.length) {
        toast.info("Not enough past spending to suggest budgets yet");
        setSuggestions([]);
      } else {
        setSuggestions(data.suggestions);
        toast.success(`Suggestions ready — based on ${data.months_analyzed} month${data.months_analyzed === 1 ? "" : "s"}`);
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Smart Budgets is a Pro feature");
      } else {
        toast.error(err?.response?.data?.detail || "Failed");
      }
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = async (s) => {
    try {
      await api.post("/budgets", {
        category: s.category,
        monthly_limit: s.suggested,
      });
      toast.success(`Set ${s.category} to ${fmtMoney(s.suggested)}`);
      setSuggestions((prev) => (prev || []).filter((x) => x.category !== s.category));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const applyAll = async () => {
    if (!suggestions?.length) return;
    if (!window.confirm(`Apply ${suggestions.length} suggested budgets? Existing budgets for these categories will be replaced.`)) return;
    try {
      await Promise.all(
        suggestions.map((s) =>
          api.post("/budgets", { category: s.category, monthly_limit: s.suggested })
        )
      );
      toast.success(`Applied ${suggestions.length} budgets`);
      setSuggestions(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Some suggestions failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="budgets-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Budgets</h1>
        <HelpButton id="budgets" />
      </div>

      {canSmart && (
        <div className="nb-card p-4 space-y-2" style={{ background: "#fdecc8" }} data-testid="smart-budgets-card">
          <div className="flex items-center gap-2">
            <Sparkles size={16} strokeWidth={3} />
            <div className="font-display font-black text-sm">Smart Budgets</div>
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            Suggests a per-category monthly limit based on your last 3 months of spend, rounded up to $5.
          </div>
          <button
            data-testid="suggest-budgets-btn"
            onClick={suggest}
            disabled={suggesting}
            className="nb-btn nb-btn-dark w-full !py-2"
          >
            <Sparkles size={14} strokeWidth={3} />
            {suggesting ? "Analyzing..." : "Auto-suggest from last 3 months"}
          </button>
          {suggestions && suggestions.length > 0 && (
            <div className="space-y-2 pt-2 border-t-2 border-[#1a1a1a]/20" data-testid="suggestions-list">
              <div className="flex justify-between items-center">
                <div className="label !mb-0 !text-[10px]">{suggestions.length} suggested</div>
                <button
                  data-testid="apply-all-suggestions-btn"
                  onClick={applyAll}
                  className="chip active !text-[10px] !py-0.5"
                >
                  Apply all
                </button>
              </div>
              {suggestions.map((s) => (
                <div
                  key={s.category}
                  className="flex items-center justify-between gap-2 bg-white rounded-lg border-2 border-[#1a1a1a] p-2"
                  data-testid={`suggestion-${s.category}`}
                >
                  <div>
                    <div className="font-display font-black text-sm">{s.category}</div>
                    <div className="text-[10px] font-medium text-[#4a4a4a]">
                      avg spend {fmtMoney(s.avg_actual)}/mo
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-display font-black text-base">{fmtMoney(s.suggested)}</div>
                    <button
                      data-testid={`apply-suggestion-${s.category}`}
                      onClick={() => applySuggestion(s)}
                      className="nb-btn nb-btn-dark !py-1 !px-2 !text-xs"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="nb-card p-5 space-y-3" data-testid="budget-form">
        <div className="flex items-center gap-2 mb-1">
          <Target size={18} strokeWidth={3} />
          <div className="label !mb-0">Set a monthly limit</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Category</label>
            <select
              data-testid="budget-category"
              className="nb-select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Limit</label>
            <input
              data-testid="budget-limit"
              type="number"
              step="0.01"
              required
              className="nb-input"
              value={form.monthly_limit}
              onChange={(e) => setForm({ ...form, monthly_limit: e.target.value })}
              placeholder="0.00"
            />
          </div>
        </div>
        <button type="submit" data-testid="add-budget-btn" className="nb-btn nb-btn-dark w-full !py-3">
          <Plus size={16} strokeWidth={3} /> Save budget
        </button>
      </form>

      <div className="nb-card overflow-hidden">
        {status?.budgets?.length === 0 && (
          <div className="p-6 text-sm text-center text-[#4a4a4a] font-medium">
            No budgets yet. Set one above to track category spending.
          </div>
        )}
        {status?.budgets?.map((b) => (
          <div
            key={b.id}
            data-testid={`budget-item-${b.category}`}
            className="p-4 border-b-2 border-[#1a1a1a]/10 last:border-0"
            style={{ background: b.over ? "#ffb3b3" : "#fff" }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {b.over && <AlertTriangle size={16} strokeWidth={3} />}
                <div className="font-display font-black text-lg">{b.category}</div>
              </div>
              <button
                data-testid={`delete-budget-${b.category}`}
                onClick={() => remove(b.id)}
                className="nb-btn nb-btn-secondary !py-1 !px-2"
              >
                <Trash2 size={14} strokeWidth={3} />
              </button>
            </div>
            <div className="flex justify-between text-sm font-bold mb-1">
              <span>{fmtMoney(b.spent)} spent</span>
              <span>{fmtMoney(b.monthly_limit)} limit</span>
            </div>
            <div className="h-3 bg-[#f4f0eb] border-2 border-[#1a1a1a] rounded-full overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(100, b.pct)}%`,
                  background: b.over ? "#c92a2a" : b.pct > 80 ? "#f59e0b" : "#1a1a1a",
                }}
              />
            </div>
            <div className="text-xs font-bold mt-1 text-[#4a4a4a]">
              {b.over
                ? `Over by ${fmtMoney(Math.abs(b.remaining))} 🚨`
                : `${fmtMoney(b.remaining)} left · ${b.pct}% used`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
